import ChartDataLabels from "chartjs-plugin-datalabels"

import { Box, Typography } from "@material-ui/core"
import { COLOR_GRAY, OVERDUE_START_COLOR, STATUS_COLORS } from "../../../constants"
import { ProjectsDashboardViewModel } from "../../api/projects-dashboard"
import { useTranslations } from "./useTranslations"
import { Bar } from "react-chartjs-2"
import { useIsSmallScreen } from "../../../hooks/use-is-small-screen"
import { getOrCreateTooltip } from "../ProjectDashboard/util"

const useOpenProjectsScheduleGraph = ({ projectsDashboard }: OpenProjectsScheduleGraphProps) => {
  const {
    totalUpcomingProjects,
    notCompletedLateProjects,
    notCompletedOnTimeProjects,
    notCompletedPastStartProjects,
    notCompletedOtherProjects,
  } = projectsDashboard
  const translations = useTranslations({
    onTimeProjectCount: notCompletedOnTimeProjects.numberOfProjects,
    pastEndProjectCount: notCompletedLateProjects.numberOfProjects,
    pastStartProjectCount: notCompletedPastStartProjects.numberOfProjects,
    noScheduleProjectCount: notCompletedOtherProjects.numberOfProjects,
  })
  const isSmallScreen = useIsSmallScreen("xs")

  const displayBarChart = totalUpcomingProjects > 0

  const onTimeColor = STATUS_COLORS.COMPLETED["1"]
  const lateColor = STATUS_COLORS.REJECTED["2"]
  const otherColor = STATUS_COLORS.NOT_STARTED["1"]
  const pastStartColor = OVERDUE_START_COLOR

  const onTimeLabel = translations.onTimeProjectsLabel
  const pastEndLabel = translations.pastEndProjectsLabel
  const pastStartLabel = translations.pastStartProjectsLabel
  const noScheduleLabel = translations.noScheduleProjectsLabel

  const labels = [
    {
      label: onTimeLabel,
      boxColor: onTimeColor,
    },
    {
      label: pastEndLabel,
      boxColor: lateColor,
    },
    {
      label: pastStartLabel,
      boxColor: pastStartColor,
    },
    {
      label: noScheduleLabel,
      boxColor: otherColor,
    },
  ]

  const data = [
    {
      value: notCompletedOnTimeProjects.numberOfProjects,
      percentage: notCompletedOnTimeProjects.percentageOfProjects.toFixed(1),
      tooltipContent: notCompletedOnTimeProjects.projects,
      backgroundColor: onTimeColor,
    },
    {
      value: notCompletedLateProjects.numberOfProjects,
      percentage: notCompletedLateProjects.percentageOfProjects.toFixed(1),
      tooltipContent: notCompletedLateProjects.projects,
      backgroundColor: lateColor,
    },
    {
      value: notCompletedPastStartProjects.numberOfProjects,
      percentage: notCompletedPastStartProjects.percentageOfProjects.toFixed(1),
      tooltipContent: notCompletedPastStartProjects.projects,
      backgroundColor: pastStartColor,
    },
    {
      value: notCompletedOtherProjects.numberOfProjects,
      percentage: notCompletedOtherProjects.percentageOfProjects.toFixed(1),
      tooltipContent: notCompletedOtherProjects.projects,
      backgroundColor: otherColor,
    },
  ]

  const chartData = {
    labels: labels.map((l) => l.label),
    datasets: [
      {
        data: data.map((d) => d.value),
        backgroundColor: data.map(() => "rgba(0,0,0,0)"),
        hoverBackgroundColor: data.map(() => "rgba(0,0,0,0)"),
        datalabels: {
          color: COLOR_GRAY,
          font: {
            size: isSmallScreen ? 12 : 16,
          },
          offset: 20,
          anchor: "start" as const,
          align: "end" as const,
          formatter: (_value: number, context: any) => {
            const label = context.chart.data.labels[context.dataIndex]
            return label
          },
        },
      },
      {
        data: data.map((d) => d.value),
        backgroundColor: data.map((d) => d.backgroundColor),
        datalabels: {
          color: "#000",
          formatter: () => "",
        },
      },
    ],
  }

  const options = {
    indexAxis: "y" as const,
    responsive: true,

    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        position: "nearest" as const,
        external: (context: any) => {
          const { chart, tooltip } = context
          const tooltipEl = getOrCreateTooltip(chart)

          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = "0"
            return
          }

          if (tooltip.body) {
            const idx = tooltip.dataPoints[0].dataIndex
            const $data = data[idx]
            const projects = $data.tooltipContent || []
            if (projects.length === 0) {
              tooltipEl.style.opacity = "0"
              return
            }
            let innerHtml = ""
            projects.forEach((project: string) => {
              innerHtml += `<div>• ${project}</div>`
            })
            tooltipEl.innerHTML = innerHtml

            const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas

            // Display, position, and set styles for font
            tooltipEl.style.opacity = 1
            tooltipEl.style.left = positionX + 20 + "px"
            tooltipEl.style.top = positionY + tooltip.caretY + "px"
          }
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: totalUpcomingProjects + 2,
        title: { display: !isSmallScreen, text: translations.numberOfProjectsLabel },
      },
      y: {
        title: { display: false },
        ticks: { display: false },
      },
    },
  }

  return {
    displayBarChart,
    labels,
    data,
    chartData,
    options,
    translations,
  }
}

const OpenProjectsScheduleGraph = ({ projectsDashboard }: OpenProjectsScheduleGraphProps) => {
  const { displayBarChart, chartData, options, translations } = useOpenProjectsScheduleGraph({ projectsDashboard })

  if (!displayBarChart) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translations.noData}
      </Typography>
    )
  }

  return (
    <Box mt={3}>
      <Bar data={chartData} options={options} plugins={[ChartDataLabels]} />
    </Box>
  )
}

export default OpenProjectsScheduleGraph
type OpenProjectsScheduleGraphProps = {
  projectsDashboard: ProjectsDashboardViewModel
}
